import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingIcon,
  Assignment as TaskIcon,
  CheckCircle as DoneIcon,
  Warning as OverdueIcon,
  Refresh as RefreshIcon,
  DateRange as DateIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  RadialBarChart,
  RadialBar,
} from 'recharts';
import { format, subDays } from 'date-fns';
import taskService from '../../services/taskService';

const COLORS = {
  done: '#10b981',
  inProgress: '#3b82f6',
  review: '#f59e0b',
  overdue: '#ef4444',
  primary: '#667eea',
};

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          bgcolor: 'white',
          p: 2,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          border: '1px solid #e2e8f0',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: 1,
                bgcolor: entry.color,
              }}
            />
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              {entry.name}: <strong style={{ color: '#1e293b' }}>{entry.value}</strong>
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  return null;
};

// Custom Legend Component
const CustomLegend = ({ payload }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2, flexWrap: 'wrap' }}>
    {payload.map((entry, index) => (
      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: 1,
            bgcolor: entry.color,
          }}
        />
        <Typography variant="caption" sx={{ color: '#475569', fontWeight: 500 }}>
          {entry.value}
        </Typography>
      </Box>
    ))}
  </Box>
);

const TaskReportView = () => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  });

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const result = await taskService.getReport(dateRange);
      if (result.success) {
        setReport(result.data);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (field, value) => {
    setDateRange((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilter = () => {
    fetchReport();
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#3b82f6';
    if (score >= 40) return '#f59e0b';
    return '#ef4444';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Xuất sắc';
    if (score >= 60) return 'Tốt';
    if (score >= 40) return 'Trung bình';
    return 'Cần cải thiện';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12 }}>
        <CircularProgress size={48} sx={{ color: '#667eea', mb: 2 }} />
        <Typography color="text.secondary">Đang tải báo cáo...</Typography>
      </Box>
    );
  }

  const { teamSummary, userReports } = report || {};

  // Prepare chart data
  const pieData = [
    { name: 'Hoàn thành', value: teamSummary?.totals?.completed || 0, color: COLORS.done },
    { name: 'Đang làm', value: teamSummary?.totals?.inProgress || 0, color: COLORS.inProgress },
    { name: 'Chờ review', value: teamSummary?.totals?.review || 0, color: COLORS.review },
    { name: 'Quá hạn', value: teamSummary?.totals?.overdue || 0, color: COLORS.overdue },
  ].filter(d => d.value > 0);

  const barData = (userReports || []).map(user => ({
    name: (user.fullName || 'N/A').split(' ').slice(-2).join(' '),
    fullName: user.fullName || 'Chưa giao',
    completed: user.completedTasks || 0,
    inProgress: user.inProgressTasks || 0,
    overdue: user.overdueTasks || 0,
  }));

  // Radial data for top performers
  const topPerformers = (userReports || [])
    .sort((a, b) => b.performanceScore - a.performanceScore)
    .slice(0, 4)
    .map((user, index) => ({
      name: (user.fullName || 'N/A').split(' ').slice(-1)[0],
      score: user.performanceScore,
      fill: [COLORS.done, COLORS.inProgress, COLORS.primary, COLORS.review][index],
    }));

  return (
    <Box>
      {/* Date Filter */}
      <Paper
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 3,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#667eea' }}>
          <DateIcon />
          <Typography variant="subtitle2" fontWeight={600}>Khoảng thời gian:</Typography>
        </Box>
        <TextField
          type="date"
          label="Từ ngày"
          size="small"
          value={dateRange.startDate}
          onChange={(e) => handleDateChange('startDate', e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'white',
            },
          }}
        />
        <TextField
          type="date"
          label="Đến ngày"
          size="small"
          value={dateRange.endDate}
          onChange={(e) => handleDateChange('endDate', e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'white',
            },
          }}
        />
        <Button
          variant="contained"
          onClick={handleApplyFilter}
          startIcon={<RefreshIcon />}
          sx={{
            bgcolor: '#667eea',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            '&:hover': { bgcolor: '#5a67d8' },
          }}
        >
          Cập nhật
        </Button>
      </Paper>

      {/* Team Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          {
            title: 'Tổng task',
            value: teamSummary?.totals?.total || 0,
            icon: TaskIcon,
            color: COLORS.inProgress,
            gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          },
          {
            title: 'Hoàn thành',
            value: teamSummary?.totals?.completed || 0,
            icon: DoneIcon,
            color: COLORS.done,
            gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          },
          {
            title: 'Quá hạn',
            value: teamSummary?.totals?.overdue || 0,
            icon: OverdueIcon,
            color: COLORS.overdue,
            gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          },
          {
            title: 'Tỷ lệ hoàn thành',
            value: `${teamSummary?.completionRate || 0}%`,
            icon: TrendingIcon,
            color: COLORS.primary,
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          },
        ].map((card, index) => (
          <Grid item xs={6} md={3} key={index}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: 'none',
                position: 'relative',
                overflow: 'visible',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 1, fontWeight: 500 }}>
                      {card.title}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        background: card.gradient,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      {card.value}
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{
                      background: card.gradient,
                      width: 52,
                      height: 52,
                      boxShadow: `0 8px 20px ${card.color}40`,
                    }}
                  >
                    <card.icon sx={{ fontSize: 26 }} />
                  </Avatar>
                </Box>
              </CardContent>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: card.gradient,
                  borderRadius: '0 0 12px 12px',
                }}
              />
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Bar Chart - User Performance */}
        <Grid item xs={12} md={8}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1e293b' }}>
              Hiệu suất theo nhân viên
            </Typography>

            {barData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <defs>
                    <linearGradient id="completedGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLORS.done} stopOpacity={1} />
                      <stop offset="100%" stopColor={COLORS.done} stopOpacity={0.7} />
                    </linearGradient>
                    <linearGradient id="inProgressGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLORS.inProgress} stopOpacity={1} />
                      <stop offset="100%" stopColor={COLORS.inProgress} stopOpacity={0.7} />
                    </linearGradient>
                    <linearGradient id="overdueGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={COLORS.overdue} stopOpacity={1} />
                      <stop offset="100%" stopColor={COLORS.overdue} stopOpacity={0.7} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: '#64748b', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend content={<CustomLegend />} />
                  <Bar
                    dataKey="completed"
                    name="Hoàn thành"
                    fill="url(#completedGrad)"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="inProgress"
                    name="Đang làm"
                    fill="url(#inProgressGrad)"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="overdue"
                    name="Quá hạn"
                    fill="url(#overdueGrad)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <TaskIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                <Typography color="text.secondary">Chưa có dữ liệu</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Pie Chart - Team Distribution */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              height: '100%',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1e293b' }}>
              Phân bố trạng thái team
            </Typography>

            {pieData.length > 0 ? (
              <Box sx={{ position: 'relative' }}>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <defs>
                      {pieData.map((entry, index) => (
                        <linearGradient key={index} id={`pieGrad${index}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={entry.color} stopOpacity={1} />
                          <stop offset="100%" stopColor={entry.color} stopOpacity={0.7} />
                        </linearGradient>
                      ))}
                    </defs>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                      animationDuration={800}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={index} fill={`url(#pieGrad${index})`} stroke="none" />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '42%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b' }}>
                    {teamSummary?.totals?.total || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tổng
                  </Typography>
                </Box>
                <CustomLegend payload={pieData.map(d => ({ value: d.name, color: d.color }))} />
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography color="text.secondary">Chưa có dữ liệu</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Top Performers */}
      {topPerformers.length > 0 && (
        <Paper
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1e293b' }}>
            Top nhân viên xuất sắc
          </Typography>
          <Grid container spacing={2}>
            {topPerformers.map((user, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 3,
                    bgcolor: user.fill + '10',
                    border: `1px solid ${user.fill}30`,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      mx: 'auto',
                      mb: 1,
                      bgcolor: user.fill,
                      fontWeight: 700,
                      fontSize: '1.2rem',
                    }}
                  >
                    #{index + 1}
                  </Avatar>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {user.name}
                  </Typography>
                  <Chip
                    label={`${user.score} điểm`}
                    size="small"
                    sx={{
                      bgcolor: user.fill + '20',
                      color: user.fill,
                      fontWeight: 700,
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* User Performance Table */}
      <Paper
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}
      >
        <Box
          sx={{
            p: 2.5,
            borderBottom: '1px solid #e2e8f0',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
            Báo cáo chi tiết nhân viên
          </Typography>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Nhân viên</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#475569' }}>Tổng task</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#475569' }}>Hoàn thành</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#475569' }}>Đang làm</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#475569' }}>Quá hạn</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#475569' }}>Tỷ lệ</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#475569' }}>Điểm</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#475569' }}>Đánh giá</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(userReports || []).map((user, index) => (
                <TableRow
                  key={index}
                  hover
                  sx={{
                    '&:hover': { bgcolor: '#f8fafc' },
                    transition: 'background-color 0.2s',
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                        }}
                      >
                        {user.fullName?.[0] || 'U'}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {user.fullName || 'Chưa giao'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600}>{user.totalTasks}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={user.completedTasks}
                      size="small"
                      sx={{
                        bgcolor: '#d1fae5',
                        color: '#059669',
                        fontWeight: 700,
                        minWidth: 32,
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.secondary">{user.inProgressTasks}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    {user.overdueTasks > 0 ? (
                      <Chip
                        label={user.overdueTasks}
                        size="small"
                        sx={{ bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 700 }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">0</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Box sx={{ width: 60 }}>
                        <LinearProgress
                          variant="determinate"
                          value={user.completionRate}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: '#f1f5f9',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              background: `linear-gradient(90deg, ${getScoreColor(user.completionRate)} 0%, ${getScoreColor(user.completionRate)}99 100%)`,
                            },
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 35 }}>
                        {user.completionRate}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={user.performanceScore}
                      size="small"
                      sx={{
                        bgcolor: getScoreColor(user.performanceScore) + '20',
                        color: getScoreColor(user.performanceScore),
                        fontWeight: 700,
                        minWidth: 40,
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={getScoreLabel(user.performanceScore)}
                      size="small"
                      sx={{
                        bgcolor: getScoreColor(user.performanceScore) + '15',
                        color: getScoreColor(user.performanceScore),
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}

              {(!userReports || userReports.length === 0) && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                    <TaskIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                    <Typography color="text.secondary">Chưa có dữ liệu báo cáo</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default TaskReportView;
